import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BadgeCheck, CheckCheck, Plus, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApi, errMsg } from "@/lib/api";
import { eur, itDate } from "@/lib/pricing";

const r2 = (n) => Math.round(n * 100) / 100;
const VAT = 1.22;
const isDraft = (q) => q.status === "bozza";
const isSent = (q) => q.status === "inviato";
const isOpen = (q) => isDraft(q) || isSent(q);
const deposit = (q) => (isOpen(q) ? 0 : r2(q.totals.totale * (+q.deposit_pct || 0) / 100));
const balance = (q) => (isOpen(q) ? 0 : r2(q.totals.totale - deposit(q)));
const credit = (q) => r2((q.deposit_paid ? 0 : deposit(q)) + (q.balance_paid ? 0 : balance(q)));
const monthOf = (q) => (q.event_date || q.created_at || "").slice(0, 7);
const TOP = ["Video", "Impianto", "Vendita"];
const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const monthLabel = (ym) => (ym ? `${MONTHS[+ym.slice(5, 7) - 1]} ${ym.slice(0, 4)}` : "Senza data");
const COLS = [
  ["bozze", "Bozze", (q) => (isDraft(q) ? q.totals.totale : 0)],
  ["inviati", "Inviati", (q) => (isSent(q) ? q.totals.totale : 0)],
  ["confermati", "Confermati", (q) => (isOpen(q) ? 0 : q.totals.totale)],
  ["acconto", "Acconto", deposit],
  ["saldo", "Saldo", balance],
  ["avere", "Tot. avere", credit],
  ["avere-iva", "Tot. avere IVA incl.", (q) => r2(credit(q) * VAT)],
];
const sumCol = (rows, fn) => r2(rows.reduce((s, q) => s + fn(q), 0));
const TD = "py-2.5 px-3 text-right font-mono text-sm whitespace-nowrap";

const Stat = ({ label, value, testId, accent }) => (
  <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
    <p className="micro-label">{label}</p>
    <p className={`mt-1.5 text-xl sm:text-2xl font-bold font-mono ${accent || ""}`} data-testid={testId}>{value}</p>
  </div>
);

const Register = ({ sent, onPick }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button data-testid="confirm-register-btn"><Plus className="h-4 w-4 mr-1" />Registra conferma</Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[min(440px,92vw)]" align="end" collisionPadding={12}>
        <Command>
          <CommandInput placeholder="Cerca preventivo inviato..." data-testid="confirm-search" />
          <CommandList className="max-h-80">
            <CommandEmpty>Nessun preventivo inviato da confermare.</CommandEmpty>
            {sent.map((q) => (
              <CommandItem key={q.id} value={`${q.code} ${q.client?.name || ""} ${q.subject || ""}`} onSelect={() => { onPick(q); setOpen(false); }} data-testid={`confirm-pick-${q.id}`}>
                <span className="font-mono text-amber-400 mr-3">{q.code}</span>
                <span className="flex-1 truncate">{q.client?.name || "Senza cliente"}<span className="text-slate-500"> · {q.subject}</span></span>
                <span className="font-mono text-xs text-slate-400 ml-2">{eur(q.totals.totale)}</span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const PayCell = ({ q, kind, amount, onPay }) => {
  if (!amount) return <td className={`${TD} text-slate-700`}>—</td>;
  const paid = !!q[`${kind}_paid`];
  return (
    <td className={`${TD} ${paid ? "bg-emerald-500/10" : ""}`}>
      <button type="button" onClick={() => !paid && onPay(q, kind, amount)} disabled={paid} aria-pressed={paid} title={paid ? "Pagato" : "Segna come pagato"}
        className={`inline-flex items-center gap-2 rounded px-1.5 py-0.5 transition-colors ${paid ? "cursor-default" : "hover:bg-slate-800"}`} data-testid={`confirmed-${kind}-paid-${q.id}`}>
        <span className={paid ? "text-emerald-300 line-through decoration-emerald-500/50" : "text-slate-200"} data-testid={`confirmed-${kind}-amount-${q.id}`}>{eur(amount)}</span>
        <CheckCheck className={`h-4 w-4 transition-colors ${paid ? "text-emerald-400" : "text-slate-600"}`} />
      </button>
    </td>
  );
};

const NoteInput = ({ q, onSave }) => {
  const [v, setV] = useState(q.report_note || "");
  useEffect(() => { setV(q.report_note || ""); }, [q.report_note]);
  return (
    <input value={v} onChange={(e) => setV(e.target.value)} onBlur={() => v !== (q.report_note || "") && onSave(q, v)}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }} placeholder="Aggiungi nota"
      className="w-full bg-transparent border border-transparent hover:border-slate-700 focus:border-amber-400 rounded px-2 py-1 text-sm outline-none transition-colors" data-testid={`report-note-${q.id}`} />
  );
};

const Row = ({ q, onPay, onUndo, onNote }) => (
  <tr className="border-b border-slate-800/70 hover:bg-slate-800/30 transition-colors" data-testid={`confirmed-row-${q.id}`}>
    <td className="py-2.5 px-3 min-w-[200px]">
      <div className="flex items-center gap-2">
        <Link to={`/preventivi/${q.id}`} className={`font-mono text-sm hover:underline ${isOpen(q) ? "text-slate-400" : "text-amber-400"}`} data-testid={`report-code-${q.id}`}>{q.code || "Bozza"}</Link>
        {isDraft(q) && <span className="text-[10px] uppercase font-mono px-1.5 rounded bg-slate-700/60 text-slate-300">bozza</span>}
        {isSent(q) && <span className="text-[10px] uppercase font-mono px-1.5 rounded bg-sky-500/15 text-sky-300">inviato</span>}
        {!isOpen(q) && !q.deposit_paid && !q.balance_paid && <Button size="icon" variant="ghost" className="h-6 w-6 ml-auto" onClick={() => onUndo(q)} title="Annulla conferma" data-testid={`confirmed-undo-${q.id}`}><Undo2 className="h-3.5 w-3.5" /></Button>}
      </div>
      <div className="text-xs text-slate-400 truncate max-w-[220px]">{q.subject || "—"}{q.event_date ? ` · ${itDate(q.event_date)}` : ""}</div>
    </td>
    <td className={`${TD} text-slate-400`} data-testid={`report-draft-${q.id}`}>{isDraft(q) ? eur(q.totals.totale) : ""}</td>
    <td className={`${TD} text-sky-300`} data-testid={`report-sent-${q.id}`}>{isSent(q) ? eur(q.totals.totale) : ""}</td>
    <td className={TD} data-testid={`confirmed-total-${q.id}`}>{isOpen(q) ? "" : eur(q.totals.totale)}</td>
    <PayCell q={q} kind="deposit" amount={deposit(q)} onPay={onPay} />
    <PayCell q={q} kind="balance" amount={balance(q)} onPay={onPay} />
    <td className={TD} data-testid={`confirmed-credit-${q.id}`}>{isOpen(q) ? "" : eur(credit(q))}</td>
    <td className={`${TD} text-red-300`} data-testid={`confirmed-credit-vat-${q.id}`}>{isOpen(q) ? "" : eur(r2(credit(q) * VAT))}</td>
    <td className="py-2.5 px-3 text-sm min-w-[160px]" data-testid={`report-client-${q.id}`}>{q.client?.name || "—"}</td>
    <td className="py-1.5 px-3 min-w-[200px]"><NoteInput q={q} onSave={onNote} /></td>
  </tr>
);

const SumRow = ({ label, rows, testId, grand }) => (
  <tr className={grand ? "bg-amber-400/10 font-bold" : "bg-slate-800/50 font-semibold"} data-testid={testId}>
    <td className={`py-2.5 px-3 text-sm ${grand ? "text-amber-300" : "text-sky-300"}`}>{label}</td>
    {COLS.map(([k, , fn]) => <td key={k} className={`${TD} ${k === "avere-iva" ? "text-red-300" : ""}`} data-testid={`${testId}-${k}`}>{eur(sumCol(rows, fn))}</td>)}
    <td colSpan={2} />
  </tr>
);

export default function ConfirmedPage() {
  const api = useApi();
  const [all, setAll] = useState(null);
  const [year, setYear] = useState("tutti");
  const load = useCallback(() => api.get("/quotes").then((r) => setAll(r.data)).catch((e) => toast.error(errMsg(e))), []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  const setStatus = async (q, status, msg) => {
    try { await api.patch(`/quotes/${q.id}/status`, { status }); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const [pending, setPending] = useState(null);
  const onPay = (q, kind, amount) => setPending({ q, kind, amount });
  const confirmPay = async () => {
    const { q, kind } = pending;
    setPending(null);
    try {
      await api.patch(`/quotes/${q.id}/payments`, { [`${kind}_paid`]: true });
      toast.success(`${kind === "deposit" ? "Acconto" : "Saldo"} pagato: tolto dal totale avere`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const onNote = async (q, report_note) => {
    try { await api.patch(`/quotes/${q.id}/report-note`, { report_note }); toast.success("Nota salvata"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const rows = useMemo(() => (all || []).filter((q) => ["accettato", "bozza", "inviato"].includes(q.status))
    .sort((a, b) => (a.event_date || a.created_at || "").localeCompare(b.event_date || b.created_at || "")), [all]);
  const years = [...new Set(rows.map((q) => monthOf(q).slice(0, 4)).filter(Boolean))].sort().reverse();
  const shown = rows.filter((q) => year === "tutti" || monthOf(q).startsWith(year));
  useEffect(() => { if (year !== "tutti" && all && !years.includes(year)) setYear("tutti"); }, [year, years, all]);
  const months = [...new Set(shown.filter((q) => !TOP.includes(q.category)).map(monthOf))];
  const confirmed = shown.filter((q) => !isOpen(q));
  const paySum = (fn, kind, paid) => sumCol(confirmed.filter((q) => !!q[`${kind}_paid`] === paid), fn);
  const sent = (all || []).filter((q) => q.status === "inviato");
  if (!all) return null;
  return (
    <div className="space-y-6 rise" data-testid="confirmed-page">
      <div className="flex flex-wrap items-end gap-3">
        <div className="mr-auto"><p className="micro-label">Video, impianti e vendite in cima · gli altri per mese di servizio · importi IVA esclusa</p><h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">Report</h1></div>
        <Select value={year} onValueChange={setYear}>
          <SelectTrigger className="w-36" data-testid="confirmed-year-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="tutti">Tutti gli anni</SelectItem>
            {years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
        <Register sent={sent} onPick={(q) => setStatus(q, "accettato", `${q.code} registrato come confermato`)} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <Stat label="Confermati" value={confirmed.length} testId="confirmed-count" />
        <Stat label="Importo confermati" value={eur(sumCol(confirmed, (q) => q.totals.totale))} testId="confirmed-sum" accent="text-amber-400" />
        <Stat label="Acconti pagati" value={eur(paySum(deposit, "deposit", true))} testId="confirmed-deposit-paid-sum" accent="text-emerald-300" />
        <Stat label="Acconti da pagare" value={eur(paySum(deposit, "deposit", false))} testId="confirmed-deposit-due-sum" accent="text-red-300" />
        <Stat label="Saldi pagati" value={eur(paySum(balance, "balance", true))} testId="confirmed-balance-paid-sum" accent="text-emerald-300" />
        <Stat label="Saldi da pagare" value={eur(paySum(balance, "balance", false))} testId="confirmed-balance-due-sum" accent="text-red-300" />
      </div>
      <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-x-auto">
        {shown.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400" data-testid="confirmed-empty">
            <BadgeCheck className="h-8 w-8 mx-auto mb-2 text-slate-600" />Nessuna bozza o preventivo confermato. Usa “Registra conferma” per aggiungere un preventivo inviato.
          </div>
        ) : (
          <table className="w-full text-left border-collapse" data-testid="report-table">
            <thead><tr className="border-b border-slate-700 bg-slate-950/60">
              <th className="py-3 px-3 micro-label">N. preventivo</th>
              {COLS.map(([k, label]) => <th key={k} className={`py-3 px-3 micro-label text-right whitespace-nowrap ${k === "avere-iva" ? "!text-red-300" : ""}`}>{label}</th>)}
              <th className="py-3 px-3 micro-label">Cliente</th><th className="py-3 px-3 micro-label">Note</th>
            </tr></thead>
            {TOP.map((cat) => {
              const list = shown.filter((q) => q.category === cat);
              if (!list.length) return null;
              return (
                <tbody key={cat} className="border-l-2 border-fuchsia-400/70" data-testid={`report-cat-${cat.toLowerCase()}`}>
                  <tr><td colSpan={10} className="pt-4 pb-1.5 px-3"><span className="text-xs font-bold uppercase tracking-widest px-2 py-0.5 rounded bg-fuchsia-500/15 text-fuchsia-300">{cat}</span></td></tr>
                  {list.map((q) => <Row key={q.id} q={q} onPay={onPay} onNote={onNote} onUndo={(x) => setStatus(x, "inviato", `Conferma di ${x.code} annullata`)} />)}
                  <SumRow label={`Totale ${cat.toLowerCase()}`} rows={list} testId={`report-cat-sum-${cat.toLowerCase()}`} />
                </tbody>
              );
            })}
            {months.map((ym) => {
              const list = shown.filter((q) => monthOf(q) === ym && !TOP.includes(q.category));
              return (
                <tbody key={ym} data-testid={`report-month-${ym || "none"}`}>
                  <tr><td colSpan={10} className="pt-4 pb-1.5 px-3 text-xs font-semibold uppercase tracking-widest text-slate-400">{monthLabel(ym)}</td></tr>
                  {list.map((q) => <Row key={q.id} q={q} onPay={onPay} onNote={onNote} onUndo={(x) => setStatus(x, "inviato", `Conferma di ${x.code} annullata`)} />)}
                  <SumRow label={`Totale ${monthLabel(ym).toLowerCase()}`} rows={list} testId={`report-month-sum-${ym || "none"}`} />
                </tbody>
              );
            })}
            <tfoot><SumRow label="Totale generale" rows={shown} testId="report-grand" grand /></tfoot>
          </table>
        )}
      </div>
      <AlertDialog open={!!pending} onOpenChange={(v) => !v && setPending(null)}>
        <AlertDialogContent className="bg-slate-900 border-slate-700" data-testid="pay-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confermi il pagamento {pending?.kind === "deposit" ? "dell'acconto" : "del saldo"}?</AlertDialogTitle>
            <AlertDialogDescription>
              {pending && <><b className="text-slate-200">{eur(pending.amount)}</b> · {pending.q.code} · {pending.q.client?.name || "—"}<br /></>}
              Il pagamento viene registrato e tolto dal totale avere. L'operazione non si può annullare.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="pay-confirm-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmPay} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="pay-confirm-ok">Sì, è stato pagato</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <p className="text-xs text-slate-500">Tot. avere = acconti e saldi non ancora pagati. IVA 22%. Clicca il doppio check accanto ad acconto o saldo quando è pagato e conferma: diventa verde, viene tolto dai totali avere e non si può più annullare.</p>
    </div>
  );
}
